import { createContext } from 'react';
import JSON5 from 'json5';
import {
  Indicator,
  INDICATOR_STAGES,
  INDICATOR_STAGES_KEY,
  IndicatorByService,
  IndicatorSectionKey,
  indicatorSectionsInOrder,
  IndicatorWithUpdate,
} from 'npo/pages/ReportBuilder/Components/ImpactIndicator/utils';
import {
  IndicatorDetails,
  SectionIndicators,
} from 'npo/pages/ReportBuilder/Components/IndicatorDetails/utils';
import { RequestState } from 'shared/auth/hooks/useRequestState';
import { requestInit } from 'shared/auth/hooks/useRequestState';
import {
  IMPACT_SUMMARY_QUESTION_ID,
  INDICATOR_DETAILS_QUESTION_ID,
  INDICATOR_SUMMARY_QUESTION_ID,
} from 'npo/pages/NPOFeedback/utils';
import { ReportAnswers } from 'donor/portal/pages/FeedbackDashboard/Feedback/FeedbackReport';
import { ReportAnswersResponse } from 'npo/pages/BetaFeedback/api';
import { LocalDeletedIndicators } from 'npo/pages/NPOFeedback/MakeEdits/components/ImpactTable/utils';
import { ReportPeriod } from 'npo/pages/ReportBuilder/Components/SectionPeriod/utils';
import { ReportStage } from 'donor/portal/pages/FeedbackDashboard/api';
import {
  getFinalizeMarkerQuestionId,
  isReportFinalized,
  isReportInterim,
} from 'npo/pages/ReportBuilder/Components/ReportFinalize/utils';
import { logError } from '../../utils/logError';

export const serviceQuestionId = 40002;
export const subServiceQuestionId = 40003;
export const capacityDevelopmentQuestionId = 40021;
export const beneficiaryTermQuestionId = 40004;
export const intermediariesQuestionId = 40022;
export const socialImpactQuestionId = 40010;
export const reachQuestionId = 40011;
export const learnQuestionId = 40007;
export const actQuestionId = 40008;

export type DeletedIndicatorEntry = {
  indicatorDetails: IndicatorDetails;
  indicatorSummary: IndicatorWithUpdate;
};

export type DeletedIndicator = {
  [indicatorId: number]: DeletedIndicatorEntry | undefined;
};

export type PropertyData<T> = {
  name: keyof T;
  value: T[keyof T];
};

export interface ResultsIndicator {
  dbId: number;
  description: string; // sources and assumptions
  forecastCurrent: number;
  forecastInitial: number;
  forecastVariance: number;
  indicatorId: number;
  measurementType: string;
  successCriteria: string; // impact summary description
  classification: string;
  currentForecastFullClaim: number;
  currentForecastFundedClaim: number;
  enabled: boolean;
  indicator: Indicator;
  timeHorizon: IndicatorDetails['timePeriod'];
  file: string | null;
  npoIndicatorFile: string | null;
  relevant: boolean;
  level1Forecast: string | null;
  level1Guess: string | null;
  level1IsHuman: string | null;
  level1ForecastResultOne: string | null;
}

export interface ResultsByReport {
  [indicatorId: number]: ResultsIndicator | undefined;
}

const noOp = () => null;
export const IndicatorContext = createContext<{
  isLoading: boolean;
  indicators: ResultsByReport;
  impactSummary: IndicatorByService;
  impactDetails: SectionIndicators;
  allIndicators: IndicatorByService;
  answers: SimpleAnswersById;
  allAnswers: ReportAnswersResponse;
  answersBySections: ReportAnswers;
  service: string | undefined;
  reportResults: ResultsIndicator[];
  reportNpoIndicators: IndicatorDetails[];
  serviceQuestionId: typeof serviceQuestionId;
  subServiceQuestionId: typeof subServiceQuestionId;
  subService: string | undefined;
  beneficiaryName: string | undefined;
  intermediaries: string | undefined;
  reportId: string | undefined;
  reportPeriod: ReportPeriod;
  reachHumanIndicator: IndicatorDetails | undefined;
  deletedIndicators: {
    [indicatorId: number]:
      | {
          indicatorDetails: IndicatorDetails;
          indicatorSummary: IndicatorWithUpdate;
        }
      | undefined;
  };
  saveRequestState: RequestState<unknown>;
  capacityDevelopment: string[] | undefined;
  reach: string[] | undefined;
  learn: 'Yes' | 'No' | undefined;
  act: 'Yes' | 'No' | undefined;
  succeed: string[] | undefined;
  localDeletedIndicators: LocalDeletedIndicators;
  capacityDevelopmentQuestionId: typeof capacityDevelopmentQuestionId;
  beneficiaryTermQuestionId: typeof beneficiaryTermQuestionId;
  intermediariesQuestionId: typeof intermediariesQuestionId;
  socialImpactQuestionId: typeof socialImpactQuestionId;
  reachQuestionId: typeof reachQuestionId;
  learnQuestionId: typeof learnQuestionId;
  actQuestionId: typeof actQuestionId;
  savingState: RequestState<unknown>;
  generateModel: () => unknown;
  isModalEmpty: () => boolean;
  handleUpdateBeneficiaryTerms: (
    beneficiaryTerm: string,
    intermediaries: string | undefined,
  ) => unknown;
  handleDeleteSection: (sectionKey: IndicatorSectionKey) => unknown;
  setAllIndicators: React.Dispatch<React.SetStateAction<IndicatorByService>>;
  setImpactSummary: React.Dispatch<React.SetStateAction<IndicatorByService>>;
  setImpactDetails: React.Dispatch<React.SetStateAction<SectionIndicators>>;
  setCapacityDevelopment: React.Dispatch<
    React.SetStateAction<string[] | undefined>
  >;
  setReach: React.Dispatch<React.SetStateAction<string[] | undefined>>;
  setLearn: React.Dispatch<React.SetStateAction<'Yes' | 'No' | undefined>>;
  setAct: React.Dispatch<React.SetStateAction<'Yes' | 'No' | undefined>>;
  setSucceed: React.Dispatch<React.SetStateAction<string[] | undefined>>;
  getMissingIndicatorDetails: (
    indicatorId: number,
    sectionKey: IndicatorSectionKey,
    indicator: IndicatorWithUpdate,
    impactSummaryIndex: number,
  ) => unknown;
  loadReportIndicators: (
    reportId: number | string,
    forceLoad?: boolean,
  ) => unknown;
  handleUpdateDescription: (
    indicatorId: number,
    sectionKey: IndicatorSectionKey,
    value: string,
  ) => Promise<boolean>;
  handleAddIndicator: (
    sectionKey: IndicatorSectionKey,
    indicator: IndicatorWithUpdate,
  ) => unknown;
  handleDeleteIndicator: (
    sectionKey: IndicatorSectionKey,
    indicatorId: number,
  ) => unknown;
  handleRestoreIndicator: (
    sectionKey: IndicatorSectionKey,
    indicatorId: number,
    summary?: IndicatorWithUpdate,
    detail?: IndicatorDetails,
  ) => unknown;
  setService: React.Dispatch<React.SetStateAction<string | undefined>>;
  setSubService: React.Dispatch<React.SetStateAction<string | undefined>>;
  setBeneficiaryName: React.Dispatch<React.SetStateAction<string | undefined>>;
  setIntermediaries: React.Dispatch<React.SetStateAction<string | undefined>>;
  getRenderForecast: (indicator: IndicatorDetails) => string;
  getSourcesAndAssumptions: (indicator: IndicatorDetails) => string;
  setIndicatorState: (questionId: number, value: string[]) => unknown;
  setLearnAndAct: (
    questionId: number,
    value: 'Yes' | 'No' | undefined,
  ) => unknown;
}>({
  isLoading: false,
  localDeletedIndicators: {
    capacityDevelopment: [],
    reach: [],
    succeed: [],
    catalyzed: [],
    learn: [],
    act: [],
  },
  indicators: {},
  answers: {},
  allAnswers: {
    answers: [],
    status: 'Draft',
    stage: 'INITIAL',
  },
  reportResults: [],
  reportNpoIndicators: [],
  answersBySections: {},
  beneficiaryName: undefined,
  intermediaries: undefined,
  reportPeriod: {
    card: undefined,
    startDate: undefined,
    endDate: undefined,
  },
  allIndicators: {
    capacityDevelopment: [],
    reach: [],
    learn: [],
    act: [],
    succeed: [],
    catalyzed: [],
  },
  serviceQuestionId,
  subServiceQuestionId,
  impactDetails: {
    capacityDevelopment: [],
    reach: [],
    learn: [],
    act: [],
    succeed: [],
    catalyzed: [],
  },
  impactSummary: {
    capacityDevelopment: [],
    reach: [],
    learn: [],
    act: [],
    succeed: [],
    catalyzed: [],
  },
  service: undefined,
  reportId: undefined,
  subService: undefined,
  capacityDevelopment: undefined,
  reach: undefined,
  learn: undefined,
  act: undefined,
  succeed: undefined,
  deletedIndicators: {},
  capacityDevelopmentQuestionId,
  beneficiaryTermQuestionId,
  intermediariesQuestionId,
  socialImpactQuestionId,
  reachQuestionId,
  learnQuestionId,
  actQuestionId,
  reachHumanIndicator: undefined,
  savingState: requestInit(),
  saveRequestState: requestInit(),
  isModalEmpty: () => true,
  getMissingIndicatorDetails: noOp,
  handleUpdateBeneficiaryTerms: noOp,
  generateModel: noOp,
  setAllIndicators: noOp,
  setImpactSummary: noOp,
  setImpactDetails: noOp,
  setIndicatorState: noOp,
  handleDeleteSection: noOp,
  setLearnAndAct: noOp,
  setBeneficiaryName: noOp,
  setIntermediaries: noOp,
  setCapacityDevelopment: noOp,
  setReach: noOp,
  setLearn: noOp,
  setAct: noOp,
  setSucceed: noOp,
  setService: noOp,
  setSubService: noOp,
  loadReportIndicators: noOp,
  getRenderForecast: () => '',
  getSourcesAndAssumptions: () => '',
  handleAddIndicator: noOp,
  handleDeleteIndicator: noOp,
  handleRestoreIndicator: noOp,
  handleUpdateDescription: () => Promise.resolve(false),
});

export const isModelEmpty = (model: IndicatorByService | SectionIndicators) => {
  return (
    model.capacityDevelopment.length === 0 &&
    model.reach.length === 0 &&
    model.learn.length === 0 &&
    model.act.length === 0 &&
    model.succeed.length === 0 &&
    model.catalyzed.length === 0
  );
};

export const getModelBulkAnswers = (
  impactSummary: IndicatorByService,
  impactDetails: SectionIndicators | undefined,
  reportStage: ReportStage,
) => {
  const interimResultQuestionId = isReportInterim(reportStage)
    ? getFinalizeMarkerQuestionId(2, reportStage)
    : undefined;
  const isFinal = isReportFinalized(reportStage);
  const capacityDevelopmentAnswer = JSON.stringify(
    impactSummary.capacityDevelopment.map(x => x.name),
  );
  const reachAnswer = JSON.stringify(impactSummary.reach.map(x => x.name));
  const learnAnswer = impactSummary.learn.length === 0 ? 'No' : 'Yes';
  const actAnswer = impactSummary.act.length === 0 ? 'No' : 'Yes';
  const succeedAnswerArray = impactSummary.succeed.map(x => x.name);
  const succeedAnswer = JSON.stringify(succeedAnswerArray);
  const indicatorDetailsAnswer = impactDetails
    ? typeof impactDetails === 'string'
      ? impactDetails
      : JSON.stringify(impactDetails)
    : undefined;
  const indicatorSummaryAnswer = JSON.stringify(impactSummary);
  const result = [
    {
      id: undefined,
      answerId: undefined,
      ignoreClearData: true,
      questionId: capacityDevelopmentQuestionId,
      text: capacityDevelopmentAnswer,
    },
    {
      id: undefined,
      answerId: undefined,
      ignoreClearData: true,
      questionId: reachQuestionId,
      text: reachAnswer,
    },
    {
      id: undefined,
      answerId: undefined,
      ignoreClearData: true,
      questionId: learnQuestionId,
      text: learnAnswer,
    },
    {
      id: undefined,
      answerId: undefined,
      ignoreClearData: true,
      questionId: actQuestionId,
      text: actAnswer,
    },
    {
      id: undefined,
      answerId: undefined,
      ignoreClearData: true,
      questionId: socialImpactQuestionId,
      text: succeedAnswer,
    },
    {
      id: undefined,
      answerId: undefined,
      ignoreClearData: true,
      questionId: IMPACT_SUMMARY_QUESTION_ID,
      text: indicatorSummaryAnswer,
    },
  ];
  if (indicatorDetailsAnswer) {
    result.push(
      {
        id: undefined,
        answerId: undefined,
        ignoreClearData: true,
        questionId: INDICATOR_DETAILS_QUESTION_ID,
        text: indicatorDetailsAnswer,
      },
      {
        id: undefined,
        answerId: undefined,
        ignoreClearData: true,
        questionId: INDICATOR_SUMMARY_QUESTION_ID,
        text: indicatorDetailsAnswer,
      },
    );
  }
  if (isFinal || interimResultQuestionId) {
    result.push({
      id: undefined,
      answerId: undefined,
      ignoreClearData: true,
      questionId: interimResultQuestionId || 90002,
      text: 'Completed',
    });
  }
  return {
    result,
    indicatorDetailsAnswer,
    indicatorSummaryAnswer,
    capacityDevelopmentAnswer,
    reachAnswer,
    learnAnswer,
    actAnswer,
    succeedAnswer,
    succeedAnswerArray,
  };
};

export const parseAnswer = (answer: string | undefined): string[] => {
  if (!answer) return [];
  try {
    return JSON5.parse(answer);
  } catch (error) {
    return [];
  }
};

export const getAllUnselectedIndicators = (
  allIndicators: Indicator[],
  impactSummary: IndicatorByService,
  capacityDevelopmentEnabled: boolean,
  learnEnabled: boolean,
  actEnabled: boolean,
): { all: IndicatorByService; unselected: IndicatorByService } => {
  const all: IndicatorByService = {
    capacityDevelopment: [],
    reach: [],
    learn: [],
    act: [],
    succeed: [],
    catalyzed: [],
  };
  const unselected: IndicatorByService = {
    capacityDevelopment: [],
    reach: [],
    learn: [],
    act: [],
    succeed: [],
    catalyzed: [],
  };
  const selectedIds: { [id: number]: boolean } = {};
  indicatorSectionsInOrder.forEach(section => {
    impactSummary[section].forEach(indicator => {
      selectedIds[indicator.id] = true;
    });
  });
  allIndicators.forEach(indicator => {
    all[INDICATOR_STAGES_KEY[indicator.stageId]].push(indicator);
    if (!selectedIds[indicator.id]) {
      unselected[INDICATOR_STAGES_KEY[indicator.stageId]].push(indicator);
    }
  });
  // if (!capacityDevelopmentEnabled) {
  //   unselected.capacityDevelopment = [];
  // }
  // if (!learnEnabled) {
  //   unselected.learn = [];
  // }
  // if (!actEnabled) {
  //   unselected.act = [];
  // }
  return {
    all,
    unselected,
  };
};

const getIndicatorBySection = (
  indicators: Indicator[],
  sectionKey: IndicatorSectionKey,
  current: string[] | undefined,
  deletedIndicators: DeletedIndicator,
): {
  allIndicators: Indicator[];
  unselectedIndicators: Indicator[];
  selectedIndicators: IndicatorWithUpdate[];
} => {
  const key = INDICATOR_STAGES[sectionKey];
  const allIndicators: Indicator[] = [];
  const unselectedIndicators: Indicator[] = [];
  const selectedIndicators: IndicatorWithUpdate[] = [];
  for (const indicator of indicators) {
    if (indicator.stageId === key) {
      allIndicators.push(indicator);
      if (current && current.includes(indicator.name)) {
        const deletedIndicator = deletedIndicators[indicator.id];
        selectedIndicators.push({
          ...indicator,
          lockDelete: deletedIndicator?.indicatorSummary.lockDelete,
          updatedText: deletedIndicator?.indicatorSummary.updatedText,
        });
      } else {
        unselectedIndicators.push(indicator);
      }
    }
  }
  return {
    allIndicators,
    selectedIndicators,
    unselectedIndicators,
  };
};

const getImpactSummaryModel = (
  indicators: Indicator[],
  capacityDevelopment: string[] | undefined,
  reach: string[] | undefined,
  learn: 'Yes' | 'No' | undefined,
  act: 'Yes' | 'No' | undefined,
  succeed: string[] | undefined,
  deletedIndicators: DeletedIndicator,
): {
  unselectedIndicators: IndicatorByService;
  selectedIndicators: IndicatorByService;
} => {
  const capacityDevelopmentEnabled =
    capacityDevelopment && capacityDevelopment.length > 0;
  const reachEnabled = reach && reach.length > 0;
  const learnEnabled = learn === 'Yes';
  const actEnabled = act === 'Yes';
  const succeedEnabled = succeed && succeed.length > 0;

  const capacityDevelopmentIndicators = getIndicatorBySection(
    indicators,
    'capacityDevelopment',
    capacityDevelopment,
    deletedIndicators,
  );
  const reachIndicators = getIndicatorBySection(
    indicators,
    'reach',
    reach,
    deletedIndicators,
  );
  const learnIndicators = getIndicatorBySection(
    indicators,
    'learn',
    undefined,
    deletedIndicators,
  );
  const actIndicators = getIndicatorBySection(
    indicators,
    'act',
    undefined,
    deletedIndicators,
  );
  const succeedIndicators = getIndicatorBySection(
    indicators,
    'succeed',
    succeed,
    deletedIndicators,
  );
  return {
    unselectedIndicators: {
      capacityDevelopment: capacityDevelopmentEnabled
        ? capacityDevelopmentIndicators.unselectedIndicators
        : [],
      reach: reachEnabled ? reachIndicators.unselectedIndicators : [],
      learn: [],
      act: [],
      succeed: succeedEnabled ? succeedIndicators.unselectedIndicators : [],
      catalyzed: [],
    },
    selectedIndicators: {
      capacityDevelopment: capacityDevelopmentEnabled
        ? capacityDevelopmentIndicators.selectedIndicators
        : [],
      reach: reachEnabled
        ? reachIndicators.selectedIndicators
        : reachIndicators.allIndicators,
      learn: learnEnabled ? learnIndicators.allIndicators : [],
      act: actEnabled ? actIndicators.allIndicators : [],
      succeed: succeedEnabled ? succeedIndicators.selectedIndicators : [],
      catalyzed: [],
    },
  };
};

const getImpactDetailIndicator = (
  indicator: Indicator,
  detailIndicators: { [id: number]: IndicatorDetails | undefined },
  deletedIndicators: DeletedIndicator,
  impactIndicators: ResultsByReport,
): IndicatorDetails => {
  if (deletedIndicators[indicator.id]) {
    return deletedIndicators[indicator.id]!.indicatorDetails;
  }
  const detail = detailIndicators[indicator.id];
  const summary = impactIndicators[indicator.id];
  return {
    indicatorId: indicator.id,
    indicatorName: indicator.name,
    indicatorDefinition: indicator.definition,
    stageId: indicator.stageId,
    finalForecast: detail?.finalForecast,
    file: detail?.file,
    forecast: detail?.forecast,
    happensAfterProgramYear: detail?.happensAfterProgramYear,
    measurement: detail?.measurement || summary?.measurementType,
    timePeriod: detail?.timePeriod,
    isHuman: detail?.isHuman || summary?.indicator.isHuman,
    interventionDosageLevel: detail?.interventionDosageLevel,
    interventionDosageNumber: detail?.interventionDosageNumber,
    interventionDosageType: detail?.interventionDosageType,
    relevant: detail?.relevant ?? true,
    level1Forecast: detail?.level1Forecast,
    level1Guess: detail?.level1Guess,
    level1IsHuman: detail?.level1IsHuman,
    level1ForecastResultOne: detail?.level1ForecastResultOne,
  };
};

export const getModal = (
  indicators: Indicator[],
  deletedIndicators: DeletedIndicator,
  capacityDevelopment: string[] | undefined,
  reach: string[] | undefined,
  learn: 'Yes' | 'No' | undefined,
  act: 'Yes' | 'No' | undefined,
  succeed: string[] | undefined,
  impactIndicators: ResultsByReport,
  detailIndicators: { [id: number]: IndicatorDetails | undefined },
): {
  unselectedIndicators: IndicatorByService;
  summary: IndicatorByService;
  details: SectionIndicators;
} => {
  const summary = getImpactSummaryModel(
    indicators,
    capacityDevelopment,
    reach,
    learn,
    act,
    succeed,
    deletedIndicators,
  );

  const details: SectionIndicators = {
    capacityDevelopment: [],
    reach: [],
    learn: [],
    act: [],
    succeed: [],
    catalyzed: [],
  };
  for (const sectionKey of indicatorSectionsInOrder) {
    summary.selectedIndicators[sectionKey].forEach(indicator => {
      details[sectionKey].push(
        getImpactDetailIndicator(
          indicator,
          detailIndicators,
          deletedIndicators,
          impactIndicators,
        ),
      );
    });
  }

  return {
    unselectedIndicators: summary.unselectedIndicators,
    summary: summary.selectedIndicators,
    details,
  };
};

export const normalizeIndicatorIds = (
  results: ResultsIndicator[] | undefined,
): ResultsIndicator[] => {
  if (!results) return [];
  return results.map(result => ({
    ...result,
    indicator: {
      ...result.indicator,
      id: result.indicator.dbId ?? result.indicator.id,
    },
  }));
};

export type SimpleAnswersById = Record<
  number,
  ReportAnswersResponse['answers'][number]['text'] | undefined
> & {
  50001?: SectionIndicators;
};

const parseQuestionId = [
  30017,
  40010,
  40011,
  40021,
  40015,
  50001,
  50002,
  60001,
  70001,
  70002,
  80003,
  80005,
  80007,
  80009,
  80011,
];
export const parseReportAnswers = (
  answers: ReportAnswersResponse | undefined,
): SimpleAnswersById => {
  const result: SimpleAnswersById = {};
  if (!answers) return result;

  for (const a of answers.answers) {
    try {
      result[a.questionId] = parseQuestionId.includes(a.questionId)
        ? JSON5.parse(a.text)
        : a.text;
    } catch (error) {
      logError('parseReportAnswers', {
        questionId: a.questionId,
        answer: a.text,
        error,
      });
    }
  }

  return result;
};

export const deduplicateNpoIndicators = (
  allNpoReportIndicators: IndicatorDetails[],
): IndicatorDetails[] => {
  const byId: {
    [id: number]: {
      index: number;
      indicator: IndicatorDetails;
    };
  } = {};
  const result: IndicatorDetails[] = [];
  for (let index = 0; index < allNpoReportIndicators.length; index++) {
    const element = allNpoReportIndicators[index];
    const id = element.indicatorId;
    if (byId[id]) {
      const existing = byId[id];
      const i = existing.indicator;
      const indicator: IndicatorDetails = {
        ...i,
        forecast: i.forecast || element.forecast,
        finalForecast: i.finalForecast || element.finalForecast,
        measurement: i.measurement || element.measurement,
        timePeriod: i.timePeriod || element.timePeriod,
        file: i.file || element.file,
        happensAfterProgramYear:
          i.happensAfterProgramYear || element.happensAfterProgramYear,
      };
      result[existing.index] = indicator;
      byId[id] = {
        index: existing.index,
        indicator,
      };
    } else {
      byId[id] = {
        index,
        indicator: element,
      };
      result.push(element);
    }
  }
  return result;
};

export const deduplicateResults = (
  allResults: ResultsIndicator[],
): ResultsIndicator[] => {
  const byId: {
    [id: number]: {
      index: number;
      indicator: ResultsIndicator;
    };
  } = {};
  const result: ResultsIndicator[] = [];

  for (let i = 0; i < allResults.length; i++) {
    const element = allResults[i];
    const id = element.indicatorId;
    if (byId[id]) {
      const existing = byId[id];
      const i = existing.indicator;
      const indicator: ResultsIndicator = {
        ...i,
        measurementType: i.measurementType || element.measurementType,
        successCriteria: i.successCriteria || element.successCriteria || '',
      };
      result[existing.index] = indicator;
      byId[id] = {
        index: existing.index,
        indicator,
      };
    } else {
      byId[id] = {
        index: i,
        indicator: element,
      };
      result.push(element);
    }
  }
  return result;
};

export type IndicatorsByStage = {
  [x in IndicatorSectionKey]: Indicator[];
};

export const uniqueIndicators = (indicators: Indicator[]): Indicator[] => {
  const indicatorsById: { [id: number]: Indicator } = {};
  const result: Indicator[] = [];
  indicators.forEach(i => {
    if (!indicatorsById[i.id]) {
      indicatorsById[i.id] = i;
      result.push(i);
    }
  });
  return result;
};

export const resultToIndicatorDetail = (
  i: ResultsIndicator,
): IndicatorDetails => {
  const indicatorDetail: IndicatorDetails = {
    stageId: i.indicator.stageId,
    indicatorDefinition: i.indicator.definition,
    indicatorId: i.indicatorId,
    indicatorName: i.indicator.name,
    forecast: i.forecastInitial,
    finalForecast: i.forecastCurrent,
    measurement: i.measurementType,
    isHuman: i.indicator.isHuman,
    timePeriod: i.timeHorizon,
    file: i.npoIndicatorFile || undefined,
    relevant: i.relevant,
    level1Forecast: i.level1Forecast || undefined,
    level1Guess: i.level1Guess || undefined,
    level1IsHuman: i.level1IsHuman || undefined,
    level1ForecastResultOne: i.level1ForecastResultOne || undefined,
  };
  return indicatorDetail;
};

export const replaceIndicatorFile = (
  indicator: IndicatorDetails,
  answers: SimpleAnswersById,
): IndicatorDetails => {
  if (indicator.file) return indicator;
  const result = { ...indicator };
  const answer = answers[50002] || answers[50001];
  if (answer && typeof answer === 'object') {
    const i = answer[INDICATOR_STAGES_KEY[result.stageId]]?.find(
      x => x.indicatorId === result.indicatorId,
    );
    if (i) {
      result.file = i.file;
    }
  }
  return result;
};

export interface ProcessedReportResults {
  reportResults: ResultsIndicator[];
  indicatorsByReport: ResultsByReport;
  summary: IndicatorByService;
  details: SectionIndicators;
  deleteIndicators: DeletedIndicator;
}

export const processReportResults = (
  results: ResultsIndicator[] | undefined,
  answers: SimpleAnswersById,
): ProcessedReportResults => {
  const reportResults = deduplicateResults(normalizeIndicatorIds(results));

  const indicatorsByReport: ResultsByReport = reportResults.reduce(
    (acc, indicator) => {
      acc[indicator.indicatorId] = { ...indicator };
      return acc;
    },
    {} as ResultsByReport,
  );

  const deleteIndicators: DeletedIndicator = {};
  const summary: IndicatorByService = {
    capacityDevelopment: [],
    reach: [],
    learn: [],
    act: [],
    succeed: [],
    catalyzed: [],
  };
  const details: SectionIndicators = {
    capacityDevelopment: [],
    reach: [],
    learn: [],
    act: [],
    succeed: [],
    catalyzed: [],
  };

  reportResults.forEach(indicator => {
    const sectionKey = INDICATOR_STAGES_KEY[indicator.indicator.stageId];
    const indicatorSummary = {
      ...indicator.indicator,
      seen: true,
      updatedText: indicator.successCriteria || '',
    };
    const indicatorDetails = replaceIndicatorFile(
      resultToIndicatorDetail(indicator),
      answers,
    );
    if (!indicator.enabled || !indicator.relevant) {
      deleteIndicators[indicator.indicatorId] = {
        indicatorSummary,
        indicatorDetails,
      };
      return;
    }
    summary[sectionKey].push(indicatorSummary);
    details[sectionKey].push(indicatorDetails);
  });

  return {
    reportResults,
    indicatorsByReport,
    summary,
    details,
    deleteIndicators,
  };
};
